import type { LucideIcon } from "lucide-react";
import {
  AlignVerticalSpaceAround,
  Code,
  Heading,
  Image,
  MousePointerClick,
  Newspaper,
  PanelBottom,
  PanelTop,
  Repeat,
  SeparatorHorizontal,
  Type,
} from "lucide-react";

/**
 * Client-side model of the block document contract compiled by
 * `internal/render/blocks` (see blocks.go — the authoritative types).
 * A prop value is a literal (string/number) or a `{bind: "path"}` object;
 * inside a repeat's children the path resolves against the current array
 * element with fallback to the root data.
 */

export type Binding = { bind: string };
export type PropValue = string | number | Binding;

export interface EditorBlock {
  /** Stable client identity; serialized as the optional wire `id`. */
  uid: string;
  type: string;
  props: Record<string, PropValue>;
  children?: EditorBlock[];
}

export interface EditorDoc {
  blocks: EditorBlock[];
}

export interface ThemeState {
  width: number;
  background_color: string;
  content_background: string;
  text_color: string;
  link_color: string;
  button_color: string;
  button_text_color: string;
  font_family: string;
  /** HTML lang attribute of the rendered email (2-letter code), default "en". */
  lang: string;
}

/** Mirrors `DefaultTheme()` in blocks.go; missing wire fields mean default. */
export const DEFAULT_THEME: ThemeState = {
  width: 600,
  background_color: "#f4f4f4",
  content_background: "#ffffff",
  text_color: "#333333",
  link_color: "#1a82e2",
  button_color: "#1a82e2",
  button_text_color: "#ffffff",
  font_family: "Helvetica, Arial, sans-serif",
  lang: "en",
};

export function isBinding(v: PropValue | undefined): v is Binding {
  return typeof v === "object" && v !== null && typeof v.bind === "string";
}

let uidCounter = 0;
export function newUid(): string {
  uidCounter += 1;
  return `b${Date.now().toString(36)}-${uidCounter}`;
}

// ---------------------------------------------------------------------------
// Block metadata (labels, prop forms, palette)
// ---------------------------------------------------------------------------

export type PropKind = "text" | "html" | "number" | "select";

export interface PropDef {
  name: string;
  label: string;
  kind: PropKind;
  /** Whether the ◇ literal ↔ binding toggle is offered. */
  bindable: boolean;
  options?: { value: string | number; label: string }[];
  placeholder?: string;
  hint?: string;
}

export interface BlockDef {
  type: string;
  label: string;
  icon: LucideIcon;
  props: PropDef[];
  /** Prop whose value summarizes the block in the list card. */
  summaryProp?: string;
  /** Rendered with an extra "Advanced users only" warning. */
  advanced?: boolean;
  /** Initial props for a freshly added block. */
  defaults?: Record<string, PropValue>;
}

export const BLOCK_DEFS: Record<string, BlockDef> = {
  "header-image": {
    type: "header-image",
    label: "Header image",
    icon: PanelTop,
    summaryProp: "src",
    props: [
      { name: "src", label: "Image URL", kind: "text", bindable: true, placeholder: "https://…/logo.png" },
      { name: "alt", label: "Alt text", kind: "text", bindable: true },
      { name: "href", label: "Link (optional)", kind: "text", bindable: true, placeholder: "https://…" },
    ],
  },
  heading: {
    type: "heading",
    label: "Heading",
    icon: Heading,
    summaryProp: "text",
    defaults: { level: 1, align: "left" },
    props: [
      { name: "text", label: "Heading text", kind: "text", bindable: true },
      {
        name: "level",
        label: "Level",
        kind: "select",
        bindable: false,
        options: [
          { value: 1, label: "1 — main" },
          { value: 2, label: "2 — smaller" },
        ],
      },
      {
        name: "align",
        label: "Alignment",
        kind: "select",
        bindable: false,
        options: [
          { value: "left", label: "Left" },
          { value: "center", label: "Center" },
        ],
      },
    ],
  },
  text: {
    type: "text",
    label: "Text",
    icon: Type,
    summaryProp: "html",
    props: [
      {
        name: "html",
        label: "Content (HTML)",
        kind: "html",
        bindable: true,
        hint: "Limited rich text — allowed tags: p, br, b, strong, i, em, u, a, ul, ol, li.",
      },
    ],
  },
  image: {
    type: "image",
    label: "Image",
    icon: Image,
    summaryProp: "src",
    props: [
      { name: "src", label: "Image URL", kind: "text", bindable: true, placeholder: "https://…" },
      { name: "alt", label: "Alt text", kind: "text", bindable: true },
      { name: "href", label: "Link (optional)", kind: "text", bindable: true },
      {
        name: "width",
        label: "Width (px, optional)",
        kind: "number",
        bindable: false,
        hint: "Empty = full content width.",
      },
    ],
  },
  "image-text": {
    type: "image-text",
    label: "Article (image + text)",
    icon: Newspaper,
    summaryProp: "title",
    props: [
      { name: "img", label: "Image URL", kind: "text", bindable: true },
      { name: "img_alt", label: "Image alt text", kind: "text", bindable: true },
      { name: "title", label: "Title", kind: "text", bindable: true },
      { name: "content", label: "Content (HTML)", kind: "html", bindable: true },
      { name: "link", label: "Link", kind: "text", bindable: true },
      {
        name: "link_label",
        label: "Link label",
        kind: "text",
        bindable: true,
        placeholder: "Learn more",
      },
    ],
  },
  button: {
    type: "button",
    label: "Button",
    icon: MousePointerClick,
    summaryProp: "label",
    defaults: { align: "center" },
    props: [
      { name: "label", label: "Label", kind: "text", bindable: true },
      { name: "href", label: "Link", kind: "text", bindable: true, placeholder: "https://…" },
      {
        name: "align",
        label: "Alignment",
        kind: "select",
        bindable: false,
        options: [
          { value: "center", label: "Center" },
          { value: "left", label: "Left" },
        ],
      },
    ],
  },
  separator: {
    type: "separator",
    label: "Separator",
    icon: SeparatorHorizontal,
    props: [],
  },
  spacer: {
    type: "spacer",
    label: "Spacer",
    icon: AlignVerticalSpaceAround,
    summaryProp: "height",
    defaults: { height: 16 },
    props: [
      { name: "height", label: "Height (px)", kind: "number", bindable: false },
    ],
  },
  footer: {
    type: "footer",
    label: "Footer",
    icon: PanelBottom,
    summaryProp: "address",
    props: [
      { name: "html", label: "Content (HTML, optional)", kind: "html", bindable: true },
      {
        name: "address",
        label: "Address (optional)",
        kind: "text",
        bindable: true,
        hint: "For marketing messages an unsubscribe link is appended below the footer automatically.",
      },
    ],
  },
  repeat: {
    type: "repeat",
    label: "Repeat",
    icon: Repeat,
    summaryProp: "source",
    defaults: { source: "items" },
    props: [
      {
        name: "source",
        label: "Data source (array)",
        kind: "text",
        bindable: false,
        placeholder: "items",
        hint: "Path to an array in the data (dot notation, e.g. order.items). The inner blocks render once per item.",
      },
    ],
  },
  "raw-html": {
    type: "raw-html",
    label: "Custom HTML",
    icon: Code,
    summaryProp: "html",
    advanced: true,
    props: [
      {
        name: "html",
        label: "HTML",
        kind: "html",
        bindable: false,
        hint: "Advanced users only — inserted verbatim (unchecked) and not reflected in the text part.",
      },
    ],
  },
};

/** Palette order for the add-block dialog. */
export const PALETTE: string[] = [
  "header-image",
  "heading",
  "text",
  "image",
  "image-text",
  "button",
  "separator",
  "spacer",
  "footer",
  "repeat",
  "raw-html",
];

/** Repeat children: everything except a nested repeat (contract forbids it). */
export const CHILD_PALETTE: string[] = PALETTE.filter((t) => t !== "repeat");

export function createBlock(type: string): EditorBlock {
  const def = BLOCK_DEFS[type];
  const block: EditorBlock = {
    uid: newUid(),
    type,
    props: { ...(def?.defaults ?? {}) },
  };
  if (type === "repeat") {
    block.children = [];
  }
  return block;
}

/** One-line summary of a block for its list card. */
export function blockSummary(block: EditorBlock): string {
  const def = BLOCK_DEFS[block.type];
  if (!def?.summaryProp) return "";
  const v = block.props[def.summaryProp];
  if (v === undefined || v === "") return "";
  if (isBinding(v)) return `◇ ${v.bind}`;
  const s = String(v);
  return s.length > 80 ? `${s.slice(0, 77)}…` : s;
}

// ---------------------------------------------------------------------------
// Wire (de)serialization
// ---------------------------------------------------------------------------

interface WireBlock {
  type?: unknown;
  id?: unknown;
  props?: unknown;
  children?: unknown;
}

function fromWireBlock(raw: WireBlock): EditorBlock | null {
  if (typeof raw !== "object" || raw === null || typeof raw.type !== "string") {
    return null;
  }
  const props: Record<string, PropValue> = {};
  if (typeof raw.props === "object" && raw.props !== null) {
    for (const [k, v] of Object.entries(raw.props as Record<string, unknown>)) {
      if (typeof v === "string" || typeof v === "number") {
        props[k] = v;
      } else if (
        typeof v === "object" &&
        v !== null &&
        typeof (v as Binding).bind === "string"
      ) {
        props[k] = { bind: (v as Binding).bind };
      }
    }
  }
  const block: EditorBlock = {
    uid: typeof raw.id === "string" && raw.id !== "" ? raw.id : newUid(),
    type: raw.type,
    props,
  };
  if (Array.isArray(raw.children)) {
    block.children = (raw.children as WireBlock[])
      .map(fromWireBlock)
      .filter((b): b is EditorBlock => b !== null);
  } else if (raw.type === "repeat") {
    block.children = [];
  }
  return block;
}

/** Tolerant parse of a stored `blocks_json`; malformed input yields an empty doc. */
export function parseBlocksJson(raw: string | undefined | null): EditorDoc {
  if (!raw) return { blocks: [] };
  try {
    const parsed = JSON.parse(raw) as { blocks?: WireBlock[] };
    if (!Array.isArray(parsed.blocks)) return { blocks: [] };
    return {
      blocks: parsed.blocks
        .map(fromWireBlock)
        .filter((b): b is EditorBlock => b !== null),
    };
  } catch {
    return { blocks: [] };
  }
}

function toWireBlock(block: EditorBlock): Record<string, unknown> {
  const props: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(block.props)) {
    if (v === "" || v === undefined) continue; // omit empty optional props
    props[k] = v;
  }
  const wire: Record<string, unknown> = { type: block.type, id: block.uid };
  if (Object.keys(props).length > 0) wire.props = props;
  if (block.children && block.children.length > 0) {
    wire.children = block.children.map(toWireBlock);
  }
  return wire;
}

export function serializeBlocksJson(doc: EditorDoc): string {
  return JSON.stringify({ version: 1, blocks: doc.blocks.map(toWireBlock) });
}

export function parseThemeJson(raw: string | undefined | null): ThemeState {
  const theme = { ...DEFAULT_THEME };
  if (!raw) return theme;
  try {
    const parsed = JSON.parse(raw) as Partial<Record<keyof ThemeState, unknown>>;
    if (typeof parsed.width === "number" && parsed.width > 0) {
      theme.width = parsed.width;
    }
    for (const key of [
      "background_color",
      "content_background",
      "text_color",
      "link_color",
      "button_color",
      "button_text_color",
      "font_family",
      "lang",
    ] as const) {
      const v = parsed[key];
      if (typeof v === "string" && v !== "") theme[key] = v;
    }
  } catch {
    // keep defaults
  }
  return theme;
}

export function serializeThemeJson(theme: ThemeState): string {
  return JSON.stringify(theme);
}
